/**
 * OverviewTabRedesign Component
 * Consolidated Overview tab with 4 main sections:
 *
 * S-1: Performance Snapshot - KPIs, Activity Trends, Revenue Pacing
 * S-2: Path to Goal - Current/Projected/Gap cards, Funnel Story
 * S-3: Scenario Calculator - Quick Scenarios, Projections, Fine-Tune Sliders
 * S-4: The Diagnosis - 4-panel analysis (Primary Issue, Secondary, Bright Spots, Full Picture)
 *
 * Created: December 29, 2025
 * Last Updated: December 29, 2025 (Phase 9 cleanup)
 */

import React, { useMemo, useState, useCallback } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, AreaChart, Area
} from 'recharts';
import {
  calculate6MonthRollingAverage,
  calculateCurrentMonthRates,
  compareMetrics,
  analyzeConversionImpact
} from '../utils/trendingCalculations';

// ============================================================
// ENHANCED SLIDER COMPONENT (Phase 7)
// Shows Current / Adjusted / Change + 6-month benchmark + status
// ============================================================

function EnhancedSlider({
  label,
  icon,
  currentValue,      // Actual current rate (readonly)
  adjustedValue,     // Slider value (state)
  onAdjust,          // Handler function
  historicalAvg,     // 6-month benchmark
  min,
  max,
  step,
  formatValue,       // e.g., (v) => `${v.toFixed(1)}%`
  formatChange,      // e.g., (v) => `${v > 0 ? '+' : ''}${v.toFixed(1)}pp`
  isPercentChange = false  // For activity slider: show % change instead of pp
}) {
  const change = adjustedValue - currentValue;
  const vsHistorical = currentValue - historicalAvg;

  const getStatus = () => {
    if (vsHistorical > 1) return { icon: '✅', text: 'Above average', color: '#22c55e' };
    if (vsHistorical < -1) return { icon: '🔴', text: `Below average (${vsHistorical.toFixed(1)}pp)`, color: '#ef4444' };
    return { icon: '➖', text: 'At average', color: '#64748b' };
  };

  const status = getStatus();

  return (
    <div style={{
      background: 'white',
      borderRadius: '10px',
      padding: '16px',
      border: '1px solid #e2e8f0'
    }}>
      {/* Title Row */}
      <div style={{
        fontSize: '14px',
        fontWeight: 600,
        color: '#1e293b',
        marginBottom: '10px',
        display: 'flex',
        alignItems: 'center',
        gap: '6px'
      }}>
        <span>{icon}</span>
        <span>{label}</span>
      </div>

      {/* Values Row */}
      <div style={{
        display: 'flex',
        gap: '16px',
        marginBottom: '10px',
        fontSize: '12px',
        flexWrap: 'wrap'
      }}>
        <div>
          <span style={{ color: '#64748b' }}>Current: </span>
          <span style={{ fontWeight: 600, color: '#1e293b' }}>
            {formatValue(currentValue)}
          </span>
        </div>
        <div>
          <span style={{ color: '#64748b' }}>Adjusted: </span>
          <span style={{
            fontWeight: 600,
            color: Math.abs(adjustedValue - currentValue) > 0.01 ? '#3b82f6' : '#1e293b'
          }}>
            {formatValue(adjustedValue)}
          </span>
        </div>
        <div>
          <span style={{ color: '#64748b' }}>Change: </span>
          <span style={{
            fontWeight: 600,
            color: change > 0.01 ? '#22c55e' : change < -0.01 ? '#ef4444' : '#64748b'
          }}>
            {formatChange(change)}
          </span>
        </div>
      </div>

      {/* Slider */}
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={adjustedValue}
        onChange={(e) => onAdjust(parseFloat(e.target.value))}
        style={{ width: '100%', cursor: 'pointer', marginBottom: '8px' }}
      />

      {/* Benchmark Row */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        fontSize: '11px',
        color: '#64748b'
      }}>
        <span>📊 6-mo avg: {formatValue(historicalAvg)}</span>
        <span style={{ color: status.color, fontWeight: 500 }}>
          {status.icon} {status.text}
        </span>
      </div>
    </div>
  );
}

// ============================================================
// MAIN COMPONENT
// ============================================================

export default function OverviewTabRedesign({
  data,
  goals,
  reps,
  month,
  year = new Date().getFullYear(),
  monthYear,
  selectedRep,
  setSelectedRep,
  developerMode
}) {
  // Calculator state (Phase 3.5)
  const [calculatorInputs, setCalculatorInputs] = useState({
    activityIncrease: 20,
    actToAppsRate: null, // Will be initialized from data
    appsToApprovedRate: null,
    approvedToFundedRate: null
  });

  // Active scenario state (Phase 6 - Clickable Scenarios)
  const [activeScenario, setActiveScenario] = useState(null); // 'A', 'B', 'C', or null

  // Helper: Use monthYear for goals (e.g., "January 2026"), fallback to legacy format
  const goalMonth = monthYear || `${month} ${year}`;

  // ============================================================
  // BUSINESS DAYS CALCULATIONS
  // ============================================================

  const businessDaysInfo = useMemo(() => {
    const today = new Date();
    const monthIndex = today.getMonth();
    const lastDay = new Date(year, monthIndex + 1, 0).getDate();

    let elapsed = 0;
    let total = 0;

    for (let day = 1; day <= lastDay; day++) {
      const date = new Date(year, monthIndex, day);
      const dayOfWeek = date.getDay();
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        total++;
        if (day <= today.getDate()) {
          elapsed++;
        }
      }
    }

    return {
      elapsed,
      total,
      remaining: total - elapsed
    };
  }, [year]);

  // ============================================================
  // HELPER: Filter data by rep (Team = all reps, otherwise specific rep)
  // ============================================================

  const filterByRep = useCallback((records, repFilter) => {
    if (!records) return [];
    if (repFilter === 'Team' || repFilter === 'All Reps') {
      return records.filter(r => reps.includes(r.Rep));
    }
    return records.filter(r => r.Rep === repFilter);
  }, [reps]);

  // ============================================================
  // HELPER: Filter data by month AND year (uses Date column)
  // ============================================================

  const filterByMonthYear = useCallback((dataArray) => {
    return dataArray?.filter(row =>
      row.Month === month && row.Date && new Date(row.Date).getFullYear() === year
    ) || [];
  }, [month, year]);

  // ============================================================
  // CONVERSION ANALYSIS (Phase 4 - Worst Conversion Identifier)
  // ============================================================

  const conversionAnalysis = useMemo(() => {
    if (!data || !data.tabs || businessDaysInfo.elapsed === 0) return null;
    return analyzeConversionImpact(data, selectedRep, month, reps, businessDaysInfo.elapsed);
  }, [data, selectedRep, month, reps, businessDaysInfo.elapsed]);

  // ============================================================
  // KPI METRICS CALCULATION (Phase 2.1)
  // ============================================================

  const kpiMetrics = useMemo(() => {
    if (!data || !data.tabs || !goals) {
      return null;
    }

    const isTeam = selectedRep === 'Team' || selectedRep === 'All Reps';

    // Filter data for current month, year, and selected rep
    const revenueData = filterByRep(
      filterByMonthYear(data.tabs.Revenue?.data),
      selectedRep
    );
    const fundedData = filterByRep(
      filterByMonthYear(data.tabs.Funded?.data),
      selectedRep
    );
    const appsData = filterByRep(
      filterByMonthYear(data.tabs.Apps_In?.data),
      selectedRep
    );
    const callsData = filterByRep(
      filterByMonthYear(data.tabs.Dials_TalkTime?.data),
      selectedRep
    );
    const smsData = filterByRep(
      filterByMonthYear(data.tabs.SMS?.data),
      selectedRep
    );

    // Current values
    const revenue = revenueData.reduce((sum, r) => sum + (parseFloat(r.Revenue) || 0), 0);
    const fundedDollars = fundedData.reduce((sum, r) => sum + (parseFloat(r.Amount) || 0), 0);
    const fundedUnits = fundedData.length;
    const appsIn = appsData.length;
    const calls = callsData.length;
    // SMS uses SUM of Count field, NOT row count!
    const sms = smsData.reduce((sum, r) => sum + (parseInt(r.Count) || 0), 0);
    // Activity Points = Calls + SMS + (Apps × 5) [post Dec 16, 2025 formula]
    const activityPoints = calls + sms + (appsIn * 5);

    // Goals - aggregate for team or get for individual rep
    const currentMonthGoals = goals.filter(g => g.Month === goalMonth);
    let revenueGoal = 0;
    let fundedGoal = 0;
    let fundedUnitsGoal = 0;
    let appsGoal = 0;
    let activityGoalDaily = 0;

    if (isTeam) {
      // Aggregate all reps' goals
      currentMonthGoals
        .filter(g => reps.includes(g.Rep))
        .forEach(g => {
          revenueGoal += parseInt(g.Revenue_Goal) || 0;
          fundedGoal += parseInt(g.Funded_Goal) || 0;
          fundedUnitsGoal += parseInt(g.Funded_Units_Goal) || 0;
          appsGoal += parseInt(g.Apps_Goal) || 0;
          activityGoalDaily += parseInt(g.Activity_Call_Goal) || 0;
        });
    } else {
      // Find specific rep's goals
      const repGoal = currentMonthGoals.find(g => g.Rep === selectedRep);
      if (repGoal) {
        revenueGoal = parseInt(repGoal.Revenue_Goal) || 0;
        fundedGoal = parseInt(repGoal.Funded_Goal) || 0;
        fundedUnitsGoal = parseInt(repGoal.Funded_Units_Goal) || 0;
        appsGoal = parseInt(repGoal.Apps_Goal) || 0;
        activityGoalDaily = parseInt(repGoal.Activity_Call_Goal) || 0;
      }
    }

    // Activity_Call_Goal is DAILY - multiply by business days for monthly goal
    const activityGoal = activityGoalDaily * businessDaysInfo.total;

    // Calculate expected values (prorated by business days elapsed)
    const paceRatio = businessDaysInfo.total > 0 ? businessDaysInfo.elapsed / businessDaysInfo.total : 0;
    const revenueExpected = revenueGoal * paceRatio;
    const fundedDollarsExpected = fundedGoal * paceRatio;
    const fundedUnitsExpected = fundedUnitsGoal * paceRatio;
    const activityExpected = activityGoal * paceRatio;
    const appsExpected = appsGoal * paceRatio;

    return {
      revenue: {
        current: revenue,
        expected: revenueExpected,
        goal: revenueGoal,
        diff: revenue - revenueExpected,
        isCurrency: true
      },
      fundedDollars: {
        current: fundedDollars,
        expected: fundedDollarsExpected,
        goal: fundedGoal,
        diff: fundedDollars - fundedDollarsExpected,
        isCurrency: true
      },
      fundedUnits: {
        current: fundedUnits,
        expected: fundedUnitsExpected,
        goal: fundedUnitsGoal,
        diff: fundedUnits - fundedUnitsExpected,
        isCurrency: false
      },
      activityPoints: {
        current: activityPoints,
        expected: activityExpected,
        goal: activityGoal,
        diff: activityPoints - activityExpected,
        isCurrency: false
      },
      appsIn: {
        current: appsIn,
        expected: appsExpected,
        goal: appsGoal,
        diff: appsIn - appsExpected,
        isCurrency: false
      }
    };
  }, [data, goals, month, selectedRep, reps, businessDaysInfo, filterByRep, filterByMonthYear, goalMonth]);

  // ============================================================
  // FORMATTING HELPERS (wrapped in useCallback to prevent re-renders)
  // ============================================================

  const formatCurrency = useCallback((value) => {
    if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(1)}M`;
    }
    if (value >= 1000) {
      return `$${(value / 1000).toFixed(0)}K`;
    }
    return `$${Math.round(value).toLocaleString()}`;
  }, []);

  const formatNumber = useCallback((value) => {
    return Math.round(value).toLocaleString();
  }, []);

  // Full currency format for KPI cards (no K/M abbreviation)
  const formatCurrencyFull = useCallback((value) => {
    return `$${Math.round(value).toLocaleString()}`;
  }, []);

  // Full diff format for KPI cards (no K/M abbreviation)
  const formatDiffFull = useCallback((diff, isCurrency) => {
    const absValue = Math.abs(diff);
    if (isCurrency) {
      return `$${Math.round(absValue).toLocaleString()}`;
    }
    return Math.round(absValue).toLocaleString();
  }, []);

  // ============================================================
  // ACTIVITY TRENDS CALCULATION (Phase 2.2)
  // ============================================================

  const activityTrends = useMemo(() => {
    if (!data || !data.tabs || businessDaysInfo.elapsed === 0) {
      return null;
    }

    // Calculate current month per-day rates
    const currentRates = calculateCurrentMonthRates(data, selectedRep, month, businessDaysInfo.elapsed);

    // Calculate 6-month rolling averages for each metric
    const callsAvg = calculate6MonthRollingAverage(data, selectedRep, month, { tab: 'Dials_TalkTime', field: null });
    const smsAvg = calculate6MonthRollingAverage(data, selectedRep, month, { tab: 'SMS', field: 'Count' });
    const appsAvg = calculate6MonthRollingAverage(data, selectedRep, month, { tab: 'Apps_In', field: null });
    const approvedAvg = calculate6MonthRollingAverage(data, selectedRep, month, { tab: 'Approved', field: null });
    const revenueAvg = calculate6MonthRollingAverage(data, selectedRep, month, { tab: 'Revenue', field: 'Revenue' });

    // Calculate Activity Points per day (current and historical)
    // Activity = Calls + SMS + (Apps × 5)
    const activityPerDay = currentRates.callsPerDay + currentRates.smsPerDay + (currentRates.appsPerDay * 5);
    const activityAvgPerDay = callsAvg.avgPerDay + smsAvg.avgPerDay + (appsAvg.avgPerDay * 5);

    // Compare current to historical
    const comparisons = {
      activity: compareMetrics(activityPerDay, activityAvgPerDay),
      calls: compareMetrics(currentRates.callsPerDay, callsAvg.avgPerDay),
      sms: compareMetrics(currentRates.smsPerDay, smsAvg.avgPerDay),
      apps: compareMetrics(currentRates.appsPerDay, appsAvg.avgPerDay),
      approved: compareMetrics(currentRates.approvedPerDay, approvedAvg.avgPerDay),
      revenue: compareMetrics(currentRates.revenuePerDay, revenueAvg.avgPerDay)
    };

    return comparisons;
  }, [data, selectedRep, month, businessDaysInfo.elapsed]);

  // ============================================================
  // LAST 5 BUSINESS DAYS SPARKLINE DATA (Phase 2.2)
  // ============================================================

  const sparklineData = useMemo(() => {
    if (!data || !data.tabs) {
      return null;
    }

    // Get last 5 business days
    const getLast5BusinessDays = () => {
      const days = [];
      const today = new Date();
      let count = 0;
      let daysBack = 0;

      while (count < 5 && daysBack < 14) {
        const date = new Date(today);
        date.setDate(today.getDate() - daysBack);
        const dayOfWeek = date.getDay();
        if (dayOfWeek !== 0 && dayOfWeek !== 6) {
          days.unshift(date.toISOString().split('T')[0]); // Format: YYYY-MM-DD
          count++;
        }
        daysBack++;
      }
      return days;
    };

    const last5Days = getLast5BusinessDays();

    // Helper to get daily totals
    const getDailyTotals = (tabName, aggregationType, field = null) => {
      const tabData = data.tabs[tabName]?.data || [];
      return last5Days.map(dateStr => {
        const dayRecords = tabData.filter(r => {
          const recordDate = r.Date?.split('T')[0];
          const matchesDate = recordDate === dateStr;
          const matchesRep = (selectedRep === 'Team' || selectedRep === 'All Reps')
            ? reps.includes(r.Rep)
            : r.Rep === selectedRep;
          return matchesDate && matchesRep;
        });

        if (aggregationType === 'count') {
          return dayRecords.length;
        }
        if (aggregationType === 'sum' && field) {
          return dayRecords.reduce((sum, r) => sum + (parseFloat(r[field]) || 0), 0);
        }
        return 0;
      });
    };

    // Get daily data for each metric
    const callsDaily = getDailyTotals('Dials_TalkTime', 'count');
    const smsDaily = getDailyTotals('SMS', 'sum', 'Count');
    const appsDaily = getDailyTotals('Apps_In', 'count');
    const approvedDaily = getDailyTotals('Approved', 'count');
    const revenueDaily = getDailyTotals('Revenue', 'sum', 'Revenue');

    // Calculate activity points for each day
    const activityDaily = last5Days.map((_, i) =>
      callsDaily[i] + smsDaily[i] + (appsDaily[i] * 5)
    );

    return {
      days: last5Days,
      activity: activityDaily,
      calls: callsDaily,
      sms: smsDaily,
      apps: appsDaily,
      approved: approvedDaily,
      revenue: revenueDaily
    };
  }, [data, selectedRep, reps]);

  // ============================================================
  // PATH TO GOAL DATA (Phase 3)
  // ============================================================

  const pathToGoalData = useMemo(() => {
    if (!data || !data.tabs || !goals || businessDaysInfo.total === 0 || businessDaysInfo.elapsed === 0) {
      return null;
    }

    const isTeam = selectedRep === 'Team' || selectedRep === 'All Reps';

    // Get current month data (with year filter from Date column)
    const revenueRecords = filterByRep(
      filterByMonthYear(data.tabs.Revenue?.data),
      selectedRep
    );
    const fundedRecords = filterByRep(
      filterByMonthYear(data.tabs.Funded?.data),
      selectedRep
    );
    const approvedRecords = filterByRep(
      filterByMonthYear(data.tabs.Approved?.data),
      selectedRep
    );
    const appsRecords = filterByRep(
      filterByMonthYear(data.tabs.Apps_In?.data),
      selectedRep
    );
    const callsRecords = filterByRep(
      filterByMonthYear(data.tabs.Dials_TalkTime?.data),
      selectedRep
    );
    const smsRecords = filterByRep(
      filterByMonthYear(data.tabs.SMS?.data),
      selectedRep
    );

    // Current counts
    const revenue = revenueRecords.reduce((sum, r) => sum + (parseFloat(r.Revenue) || 0), 0);
    const funded = fundedRecords.length;
    const approved = approvedRecords.length;
    const apps = appsRecords.length;
    const calls = callsRecords.length;
    const sms = smsRecords.reduce((sum, r) => sum + (parseInt(r.Count) || 0), 0);
    const activity = calls + sms + (apps * 5);

    // Get 6-month historical averages for conversion rates (moved before current rates so histAvgDealSize can be used as fallback)
    const historicalAppsToApproved = calculate6MonthRollingAverage(data, selectedRep, month, { tab: 'Approved', field: null });
    const historicalApps = calculate6MonthRollingAverage(data, selectedRep, month, { tab: 'Apps_In', field: null });
    const historicalFunded = calculate6MonthRollingAverage(data, selectedRep, month, { tab: 'Funded', field: null });
    const historicalRevenue = calculate6MonthRollingAverage(data, selectedRep, month, { tab: 'Revenue', field: 'Revenue' });
    const historicalCalls = calculate6MonthRollingAverage(data, selectedRep, month, { tab: 'Dials_TalkTime', field: null });
    const historicalSms = calculate6MonthRollingAverage(data, selectedRep, month, { tab: 'SMS', field: 'Count' });

    // Calculate historical conversion rates (based on per-day averages)
    const histAppsPerDay = historicalApps.avgPerDay || 0;
    const histApprovedPerDay = historicalAppsToApproved.avgPerDay || 0;
    const histFundedPerDay = historicalFunded.avgPerDay || 0;
    const histRevenuePerDay = historicalRevenue.avgPerDay || 0;
    const histCallsPerDay = historicalCalls.avgPerDay || 0;
    const histSmsPerDay = historicalSms.avgPerDay || 0;
    const histActivityPerDay = histCallsPerDay + histSmsPerDay + (histAppsPerDay * 5);

    const histActToAppsRate = histActivityPerDay > 0 ? (histAppsPerDay / histActivityPerDay) * 100 : 1.9;
    const histAppsToApprovedRate = histAppsPerDay > 0 ? (histApprovedPerDay / histAppsPerDay) * 100 : 63.5;
    const histApprovedToFundedRate = histApprovedPerDay > 0 ? (histFundedPerDay / histApprovedPerDay) * 100 : 50.5;
    const histAvgDealSize = histFundedPerDay > 0 ? histRevenuePerDay / histFundedPerDay : 4500;

    // Calculate conversion rates (current month)
    const activityToAppsRate = activity > 0 ? (apps / activity) * 100 : 0;
    const appsToApprovedRate = apps > 0 ? (approved / apps) * 100 : 0;
    const approvedToFundedRate = approved > 0 ? (funded / approved) * 100 : 0;
    // FIX: Use historical avg deal size as fallback when no funded deals (prevents $0 projections for new reps)
    const avgDealSize = funded > 0 ? revenue / funded : histAvgDealSize;

    // Get revenue goal
    const currentMonthGoals = goals.filter(g => g.Month === goalMonth);
    let revenueGoal = 0;
    if (isTeam) {
      currentMonthGoals.filter(g => reps.includes(g.Rep)).forEach(g => {
        revenueGoal += parseInt(g.Revenue_Goal) || 0;
      });
    } else {
      const repGoal = currentMonthGoals.find(g => g.Rep === selectedRep);
      revenueGoal = repGoal ? (parseInt(repGoal.Revenue_Goal) || 0) : 0;
    }

    // Calculate projections (linear: current / elapsed * total)
    const dailyRate = revenue / businessDaysInfo.elapsed;
    const projectedRevenue = dailyRate * businessDaysInfo.total;
    const projectedFunded = Math.round((funded / businessDaysInfo.elapsed) * businessDaysInfo.total);
    const projectedApproved = Math.round((approved / businessDaysInfo.elapsed) * businessDaysInfo.total);
    const projectedApps = Math.round((apps / businessDaysInfo.elapsed) * businessDaysInfo.total);
    const gapToGoal = revenueGoal - projectedRevenue;
    const percentOfGoal = revenueGoal > 0 ? (projectedRevenue / revenueGoal) * 100 : 0;

    // Calculate deals needed to close gap
    const dealsNeeded = avgDealSize > 0 ? Math.ceil(gapToGoal / avgDealSize) : 0;

    // Scenario calculations
    const remainingDays = businessDaysInfo.remaining;

    // Scenario A: Volume Play (+20% activity, same conversion rates)
    const scenarioA = (() => {
      const activityPerDay = activity / businessDaysInfo.elapsed;
      const boostedActivity = activityPerDay * 1.2 * remainingDays; // +20% for remaining days
      const extraApps = boostedActivity * (activityToAppsRate / 100);
      const extraApproved = extraApps * (appsToApprovedRate / 100);
      const extraFunded = extraApproved * (approvedToFundedRate / 100);
      const extraRevenue = extraFunded * avgDealSize;
      return {
        impact: extraRevenue,
        extraApps: Math.round(extraApps),
        extraFunded: Math.round(extraFunded),
        closesPercent: gapToGoal > 0 ? (extraRevenue / gapToGoal) * 100 : 0
      };
    })();

    // Scenario B: Conversion Fix (dynamically targets the WORST conversion by revenue impact)
    // Uses analyzeConversionImpact from Phase 3 to identify which conversion hurts most
    const scenarioB = (() => {
      // Get worst conversion from conversionAnalysis (calculated in separate useMemo)
      const worstConv = conversionAnalysis?.worstConversion;

      // If no worst conversion (all healthy or no data), use fallback
      if (!worstConv) {
        return {
          impact: 0,
          currentRate: appsToApprovedRate,
          targetRate: histAppsToApprovedRate,
          extraFunded: 0,
          closesPercent: 0,
          conversionName: null,  // Signals "all healthy" state
          isAllHealthy: true
        };
      }

      // Use the revenue impact and details from the worst conversion
      return {
        impact: worstConv.revenueImpact,
        currentRate: worstConv.currentRate,
        targetRate: worstConv.historicalRate,
        extraFunded: Math.round(worstConv.extraDeals),
        closesPercent: gapToGoal > 0 ? (worstConv.revenueImpact / gapToGoal) * 100 : 0,
        conversionName: worstConv.name,  // Dynamic: 'Activity → Apps', 'Apps → Approved', or 'Approved → Funded'
        isAllHealthy: false
      };
    })();

    // Scenario C: Hybrid (both volume and conversion)
    const scenarioC = (() => {
      const combinedImpact = scenarioA.impact + scenarioB.impact;
      const combinedFunded = scenarioA.extraFunded + scenarioB.extraFunded;
      const projectedTotal = projectedRevenue + combinedImpact;
      return {
        impact: combinedImpact,
        extraFunded: combinedFunded,
        closesPercent: gapToGoal > 0 ? (combinedImpact / gapToGoal) * 100 : 0,
        projectedTotal,
        projectedPercent: revenueGoal > 0 ? (projectedTotal / revenueGoal) * 100 : 0
      };
    })();

    return {
      // Current metrics
      activity,
      apps,
      approved,
      funded,
      revenue,
      // Conversion rates
      activityToAppsRate,
      appsToApprovedRate,
      approvedToFundedRate,
      avgDealSize,
      // Historical rates
      histActToAppsRate,
      histAppsToApprovedRate,
      histApprovedToFundedRate,
      histAvgDealSize,
      // Projections
      projectedRevenue,
      projectedFunded,
      projectedApproved,
      projectedApps,
      revenueGoal,
      gapToGoal,
      percentOfGoal,
      dealsNeeded,
      // Scenarios
      scenarioA,
      scenarioB,
      scenarioC
    };
  }, [data, goals, month, selectedRep, reps, businessDaysInfo, filterByRep, filterByMonthYear, goalMonth, conversionAnalysis]);

  // ============================================================
  // CALCULATOR PROJECTIONS (Phase 3.5)
  // ============================================================

  const calculatorProjections = useMemo(() => {
    if (!pathToGoalData) return null;

    // Use current rates as defaults if calculator inputs aren't set yet
    const actIncrease = calculatorInputs.activityIncrease;
    const actToApps = calculatorInputs.actToAppsRate ?? pathToGoalData.histActToAppsRate;
    const appsToApproved = calculatorInputs.appsToApprovedRate ?? pathToGoalData.histAppsToApprovedRate;
    const approvedToFunded = calculatorInputs.approvedToFundedRate ?? pathToGoalData.histApprovedToFundedRate;

    // FIX: Project activity to end of month FIRST, then apply boost
    // Previously used current MTD activity, causing scenario to show lower numbers than baseline
    const projectedActivity = businessDaysInfo.elapsed > 0
      ? (pathToGoalData.activity / businessDaysInfo.elapsed) * businessDaysInfo.total
      : pathToGoalData.activity;
    const boostedActivity = projectedActivity * (1 + actIncrease / 100);
    const projApps = boostedActivity * (actToApps / 100);
    const projApproved = projApps * (appsToApproved / 100);
    const projFunded = projApproved * (approvedToFunded / 100);
    const projRevenue = projFunded * pathToGoalData.avgDealSize;

    const gapRemaining = pathToGoalData.revenueGoal - projRevenue;
    const percentToGoal = pathToGoalData.revenueGoal > 0 ? (projRevenue / pathToGoalData.revenueGoal) * 100 : 0;

    return {
      projApps: Math.round(projApps),
      projApproved: Math.round(projApproved),
      projFunded: Math.round(projFunded),
      projRevenue,
      gapRemaining,
      percentToGoal
    };
  }, [pathToGoalData, calculatorInputs, businessDaysInfo]);

  // ============================================================
  // SCENARIO CLICK HANDLERS (Phase 6 - Clickable Scenarios)
  // ============================================================

  // Handler for Scenario A (Volume Play): +20% activity, keep current conversions
  const handleScenarioA = useCallback(() => {
    if (!pathToGoalData) return;
    setCalculatorInputs({
      activityIncrease: 20,
      actToAppsRate: pathToGoalData.activityToAppsRate,
      appsToApprovedRate: pathToGoalData.appsToApprovedRate,
      approvedToFundedRate: pathToGoalData.approvedToFundedRate
    });
    setActiveScenario('A');
  }, [pathToGoalData]);

  // Handler for Scenario B (Fix Worst Conversion): fix worst conversion to historical
  const handleScenarioB = useCallback(() => {
    if (!pathToGoalData) return;
    const worst = conversionAnalysis?.worstConversion;
    if (!worst) return; // All healthy, nothing to fix

    // Start with current values
    const newInputs = {
      activityIncrease: 0, // No activity boost
      actToAppsRate: pathToGoalData.activityToAppsRate,
      appsToApprovedRate: pathToGoalData.appsToApprovedRate,
      approvedToFundedRate: pathToGoalData.approvedToFundedRate
    };

    // Fix the worst conversion to its historical rate
    if (worst.name === 'Activity → Apps') {
      newInputs.actToAppsRate = worst.historicalRate;
    } else if (worst.name === 'Apps → Approved') {
      newInputs.appsToApprovedRate = worst.historicalRate;
    } else if (worst.name === 'Approved → Funded') {
      newInputs.approvedToFundedRate = worst.historicalRate;
    }

    setCalculatorInputs(newInputs);
    setActiveScenario('B');
  }, [pathToGoalData, conversionAnalysis]);

  // Handler for Scenario C (Hybrid): +20% activity AND fix worst conversion
  const handleScenarioC = useCallback(() => {
    if (!pathToGoalData) return;
    const worst = conversionAnalysis?.worstConversion;

    // Start with current values + activity boost
    const newInputs = {
      activityIncrease: 20,
      actToAppsRate: pathToGoalData.activityToAppsRate,
      appsToApprovedRate: pathToGoalData.appsToApprovedRate,
      approvedToFundedRate: pathToGoalData.approvedToFundedRate
    };

    // Fix the worst conversion if there is one
    if (worst) {
      if (worst.name === 'Activity → Apps') {
        newInputs.actToAppsRate = worst.historicalRate;
      } else if (worst.name === 'Apps → Approved') {
        newInputs.appsToApprovedRate = worst.historicalRate;
      } else if (worst.name === 'Approved → Funded') {
        newInputs.approvedToFundedRate = worst.historicalRate;
      }
    }

    setCalculatorInputs(newInputs);
    setActiveScenario('C');
  }, [pathToGoalData, conversionAnalysis]);

  // Handler for Reset: return all sliders to current values
  const handleScenarioReset = useCallback(() => {
    if (!pathToGoalData) return;
    setCalculatorInputs({
      activityIncrease: 0,
      actToAppsRate: pathToGoalData.activityToAppsRate,
      appsToApprovedRate: pathToGoalData.appsToApprovedRate,
      approvedToFundedRate: pathToGoalData.approvedToFundedRate
    });
    setActiveScenario(null);
  }, [pathToGoalData]);

  // ============================================================
  // REVENUE PACING CHART DATA (Phase 2.3)
  // ============================================================

  const revenuePacingData = useMemo(() => {
    if (!data || !data.tabs || !goals || businessDaysInfo.total === 0) {
      return null;
    }

    const isTeam = selectedRep === 'Team' || selectedRep === 'All Reps';

    // Get revenue goal
    const currentMonthGoals = goals.filter(g => g.Month === goalMonth);
    let revenueGoal = 0;
    if (isTeam) {
      currentMonthGoals
        .filter(g => reps.includes(g.Rep))
        .forEach(g => {
          revenueGoal += parseInt(g.Revenue_Goal) || 0;
        });
    } else {
      const repGoal = currentMonthGoals.find(g => g.Rep === selectedRep);
      revenueGoal = repGoal ? (parseInt(repGoal.Revenue_Goal) || 0) : 0;
    }

    // Get all revenue records for this month (with year filter from Date column)
    const revenueRecords = (data.tabs.Revenue?.data || []).filter(r => {
      const matchesMonth = r.Month === month;
      const matchesYear = r.Date && new Date(r.Date).getFullYear() === year;
      const matchesRep = isTeam ? reps.includes(r.Rep) : r.Rep === selectedRep;
      return matchesMonth && matchesYear && matchesRep;
    });

    // Build list of business days in the month
    const today = new Date();
    const monthIndex = today.getMonth();
    const lastDayOfMonth = new Date(year, monthIndex + 1, 0).getDate();
    const monthShortNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    const businessDays = [];
    for (let day = 1; day <= lastDayOfMonth; day++) {
      const date = new Date(year, monthIndex, day);
      const dayOfWeek = date.getDay();
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        businessDays.push({
          dayNum: businessDays.length + 1,
          date: date.toISOString().split('T')[0],
          dayOfMonth: day,
          isPast: day <= today.getDate()
        });
      }
    }

    // Calculate cumulative actual revenue for each business day
    let cumulativeRevenue = 0;
    const chartData = businessDays.map(bd => {
      // Add revenue for this day
      const dayRevenue = revenueRecords
        .filter(r => {
          const recordDate = r.Date?.split('T')[0];
          if (!recordDate) return false;
          const recordDayOfMonth = parseInt(recordDate.split('-')[2]);
          return recordDayOfMonth === bd.dayOfMonth;
        })
        .reduce((sum, r) => sum + (parseFloat(r.Revenue) || 0), 0);

      if (bd.isPast) {
        cumulativeRevenue += dayRevenue;
      }

      // Required pace (linear from 0 to goal)
      const requiredPace = (revenueGoal / businessDaysInfo.total) * bd.dayNum;

      // Current pace projection (spans ALL days as linear projection from Day 1)
      const dailyRate = businessDaysInfo.elapsed > 0
        ? cumulativeRevenue / businessDaysInfo.elapsed
        : 0;
      const currentPace = dailyRate * bd.dayNum;

      return {
        day: `${monthShortNames[monthIndex]} ${bd.dayOfMonth}`,
        dayNum: bd.dayNum,
        actual: bd.isPast ? cumulativeRevenue : null,
        requiredPace: Math.round(requiredPace),
        currentPace: Math.round(currentPace),
        isToday: bd.dayNum === businessDaysInfo.elapsed
      };
    });

    // Calculate projected end values
    const currentRevenue = cumulativeRevenue;
    const dailyRate = businessDaysInfo.elapsed > 0 ? currentRevenue / businessDaysInfo.elapsed : 0;
    const projectedEnd = dailyRate * businessDaysInfo.total;

    return {
      chartData,
      revenueGoal,
      currentRevenue,
      projectedEnd,
      gapToGoal: revenueGoal - projectedEnd,
      percentOfGoal: revenueGoal > 0 ? (projectedEnd / revenueGoal) * 100 : 0
    };
  }, [data, goals, month, selectedRep, reps, businessDaysInfo, goalMonth, year]);

  // ============================================================
  // LOADING / ERROR STATES
  // ============================================================

  if (!data || !data.tabs) {
    return (
      <div className="overview-tab" style={{ padding: '40px', textAlign: 'center' }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
        <h2 style={{ color: '#64748b' }}>Loading dashboard data...</h2>
      </div>
    );
  }

  if (!goals || goals.length === 0) {
    return (
      <div className="overview-tab" style={{ padding: '40px', textAlign: 'center' }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
        <h2 style={{ color: '#f59e0b' }}>No goals data available</h2>
        <p style={{ color: '#64748b' }}>Please ensure goals are configured for {month}</p>
      </div>
    );
  }

  // ============================================================
  // RENDER
  // ============================================================

  return (
    <div className="overview-tab-redesign">
      {/* ========== WELCOME BANNER ========== */}
      <div style={{
        background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
        border: '1px solid #bfdbfe',
        borderRadius: '12px',
        padding: '20px 24px',
        marginBottom: '20px'
      }}>
        <h2 style={{
          fontSize: '18px',
          fontWeight: 600,
          color: '#1e40af',
          margin: '0 0 4px 0'
        }}>
          Welcome to the Renewals Sales Dashboard
        </h2>
        <p style={{
          fontSize: '14px',
          color: '#3b82f6',
          margin: 0
        }}>
          Track your month-to-date performance, see what you need to hit your goals, and get actionable insights to improve.
        </p>
      </div>

      {/* ========== SECTION 1: PERFORMANCE SNAPSHOT ========== */}
      <div className="overview-section-1" style={{
        background: 'white',
        borderRadius: '12px',
        padding: '24px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        border: '1px solid #e2e8f0',
        marginBottom: '20px',
        position: 'relative'
      }}>
        {developerMode && <div className="dev-section-label">S-1</div>}

        {/* Section Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px'
        }}>
          <div>
            <div style={{
              fontSize: '22px',
              fontWeight: 700,
              color: '#1e293b',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              📊 Performance Snapshot
            </div>
            <div style={{
              fontSize: '15px',
              color: '#64748b',
              marginTop: '2px'
            }}>
              {month} MTD • Day {businessDaysInfo.elapsed} of {businessDaysInfo.total} business days
            </div>
          </div>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            background: '#f1f5f9',
            padding: '4px 12px',
            borderRadius: '20px',
            fontSize: '12px',
            color: '#64748b'
          }}>
            <span>👥</span> {selectedRep === 'All Reps' ? 'Team' : selectedRep.split(' ')[0]}
          </div>
        </div>

        {/* KPI Cards - 5 base + AOV when data available */}
        <div className="overview-kpi-cards" style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${pathToGoalData ? 6 : 5}, 1fr)`,
          gap: '16px',
          marginBottom: '24px'
        }}>
          {kpiMetrics && [
            { key: 'revenue', label: 'Revenue', data: kpiMetrics.revenue },
            { key: 'fundedDollars', label: 'Funded Dollars', data: kpiMetrics.fundedDollars },
            { key: 'fundedUnits', label: 'Funded Units', data: kpiMetrics.fundedUnits },
            { key: 'activityPoints', label: 'Activity Points', data: kpiMetrics.activityPoints },
            { key: 'appsIn', label: 'Apps In', data: kpiMetrics.appsIn },
            ...(pathToGoalData ? [{
              key: 'aov',
              label: 'Avg Deal Size',
              data: {
                current: pathToGoalData.avgDealSize,
                expected: pathToGoalData.histAvgDealSize,
                goal: pathToGoalData.histAvgDealSize,
                diff: pathToGoalData.avgDealSize - pathToGoalData.histAvgDealSize,
                isCurrency: true
              }
            }] : [])
          ].map((kpi, i) => {
            const isAhead = kpi.data.diff >= 0;
            const statusColor = isAhead ? '#10b981' : '#ef4444';
            const statusIcon = isAhead ? '▲' : '▼';
            const statusText = isAhead ? 'Ahead' : 'Behind';

            return (
              <div key={kpi.key} style={{
                background: '#f8fafc',
                borderRadius: '10px',
                padding: '16px',
                border: '1px solid #e2e8f0',
                position: 'relative'
              }}>
                {developerMode && <div className="dev-card-label">K-{i + 1}</div>}
                <div style={{
                  fontSize: '12px',
                  fontWeight: 600,
                  color: '#64748b',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: '6px'
                }}>
                  {kpi.label}
                </div>
                <div style={{
                  fontSize: '42px',
                  fontWeight: 700,
                  color: '#1e293b',
                  marginBottom: '8px'
                }}>
                  {kpi.data.isCurrency
                    ? formatCurrencyFull(kpi.data.current)
                    : formatNumber(kpi.data.current)}
                </div>
                <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '4px' }}>
                  Expected: <span style={{ fontWeight: 600, fontSize: '14px' }}>{kpi.data.isCurrency
                    ? formatCurrencyFull(kpi.data.expected)
                    : formatNumber(kpi.data.expected)}</span>
                </div>
                <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '8px' }}>
                  Goal: <span style={{ fontWeight: 600, fontSize: '14px' }}>{kpi.data.isCurrency
                    ? formatCurrencyFull(kpi.data.goal)
                    : formatNumber(kpi.data.goal)}</span>
                </div>
                <div style={{ fontSize: '18px', fontWeight: 700, color: statusColor }}>
                  {statusIcon} {statusText} {formatDiffFull(kpi.data.diff, kpi.data.isCurrency)}
                </div>
              </div>
            );
          })}
        </div>

        {/* 6 Activity Trends */}
        <div className="overview-activity-trends" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(6, 1fr)',
          gap: '12px',
          marginBottom: '24px'
        }}>
          {activityTrends && sparklineData && [
            { key: 'activity', label: 'Activity/Day', data: activityTrends.activity, sparkline: sparklineData.activity, isCurrency: false },
            { key: 'calls', label: 'Calls/Day', data: activityTrends.calls, sparkline: sparklineData.calls, isCurrency: false },
            { key: 'sms', label: 'SMS/Day', data: activityTrends.sms, sparkline: sparklineData.sms, isCurrency: false },
            { key: 'apps', label: 'Apps/Day', data: activityTrends.apps, sparkline: sparklineData.apps, isCurrency: false },
            { key: 'approved', label: 'Approved/Day', data: activityTrends.approved, sparkline: sparklineData.approved, isCurrency: false },
            { key: 'revenue', label: 'Revenue/Day', data: activityTrends.revenue, sparkline: sparklineData.revenue, isCurrency: true }
          ].map((trend, i) => {
            const isPositive = trend.data.variancePercent >= 0;
            const trendColor = trend.data.trend === 'up' ? '#10b981' :
                              trend.data.trend === 'down' ? '#ef4444' : '#64748b';

            // Format the current value
            const formatValue = (val, isCurrency) => {
              if (isCurrency) {
                if (val >= 1000) return `$${(val / 1000).toFixed(1)}K`;
                return `$${Math.round(val).toLocaleString()}`;
              }
              return val >= 10 ? Math.round(val).toLocaleString() : val.toFixed(1);
            };

            // Format the average
            const formatAvg = (val, isCurrency) => {
              if (isCurrency) {
                if (val >= 1000) return `$${(val / 1000).toFixed(1)}K`;
                return `$${Math.round(val)}`;
              }
              return val >= 10 ? Math.round(val) : val.toFixed(1);
            };

            // Mini sparkline chart data
            const sparklineChartData = trend.sparkline.map((val, idx) => ({
              day: idx,
              value: val
            }));

            return (
              <div key={trend.key} style={{
                textAlign: 'center',
                padding: '14px 10px',
                background: '#f8fafc',
                borderRadius: '8px',
                border: '2px solid transparent',
                cursor: 'pointer',
                transition: 'all 0.2s',
                position: 'relative'
              }}>
                {developerMode && <div className="dev-chart-label">C-{i + 1}</div>}
                <div style={{
                  fontSize: '13px',
                  fontWeight: 600,
                  color: '#64748b',
                  textTransform: 'uppercase',
                  marginBottom: '6px'
                }}>
                  {trend.label}
                </div>
                <div style={{
                  fontSize: '32px',
                  fontWeight: 700,
                  color: '#1e293b',
                  marginBottom: '4px'
                }}>
                  {formatValue(trend.data.value, trend.isCurrency)}
                </div>
                <div style={{
                  fontSize: '14px',
                  color: trendColor,
                  lineHeight: '1.4'
                }}>
                  {isPositive ? '+' : ''}{trend.data.variance.toFixed(1)} vs {formatAvg(trend.data.historicalAvg, trend.isCurrency)} avg
                  <br/>
                  <span style={{ fontWeight: 600 }}>{isPositive ? '+' : ''}{trend.data.variancePercent.toFixed(1)}%</span>
                </div>
                {/* Mini Sparkline */}
                <div style={{ height: '55px', marginTop: '8px' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={sparklineChartData} margin={{ top: 2, right: 2, left: 2, bottom: 2 }}>
                      <defs>
                        <linearGradient id={`sparkGradient-${trend.key}`} x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={trendColor} stopOpacity={0.3}/>
                          <stop offset="95%" stopColor={trendColor} stopOpacity={0.05}/>
                        </linearGradient>
                      </defs>
                      <Area
                        type="monotone"
                        dataKey="value"
                        stroke={trendColor}
                        strokeWidth={1.5}
                        fill={`url(#sparkGradient-${trend.key})`}
                        dot={false}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>
            );
          })}
        </div>

        {/* Revenue Pacing Chart */}
        {revenuePacingData && (
          <div style={{
            background: '#f8fafc',
            borderRadius: '10px',
            padding: '16px',
            border: '1px solid #e2e8f0',
            position: 'relative'
          }}>
            {developerMode && <div className="dev-chart-label">C-7</div>}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              marginBottom: '12px'
            }}>
              <div style={{
                fontSize: '15px',
                fontWeight: 600,
                color: '#64748b'
              }}>
                📈 Revenue Pacing - Actual vs Current Pace vs Required Pace
              </div>
              <div style={{ fontSize: '16px', color: '#64748b', textAlign: 'right' }}>
                <span style={{ color: '#3b82f6', fontWeight: 600 }}>
                  {formatCurrency(revenuePacingData.currentRevenue)}
                </span>
                {' → '}
                <span style={{ color: '#94a3b8' }}>
                  {formatCurrency(revenuePacingData.projectedEnd)} proj
                </span>
                {' | Goal: '}
                <span style={{ color: '#10b981', fontWeight: 600 }}>
                  {formatCurrency(revenuePacingData.revenueGoal)}
                </span>
              </div>
            </div>

            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={revenuePacingData.chartData} margin={{ top: 10, right: 30, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis
                  dataKey="day"
                  tick={{ fontSize: 10, fill: '#64748b' }}
                  tickLine={{ stroke: '#e2e8f0' }}
                  interval="preserveStartEnd"
                />
                <YAxis
                  tickFormatter={(v) => v >= 1000 ? `$${(v / 1000).toFixed(0)}K` : `$${v}`}
                  tick={{ fontSize: 10, fill: '#64748b' }}
                  tickLine={{ stroke: '#e2e8f0' }}
                  domain={[0, 'auto']}
                />
                <Tooltip
                  formatter={(value, name) => {
                    if (value === null) return ['-', name];
                    return [`$${value.toLocaleString()}`, name];
                  }}
                  labelFormatter={(label) => label}
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    fontSize: '12px'
                  }}
                />

                {/* Required Pace (dashed green) - the target line */}
                <Line
                  type="monotone"
                  dataKey="requiredPace"
                  stroke="#10b981"
                  strokeWidth={2}
                  strokeDasharray="8 4"
                  name="Required Pace"
                  dot={false}
                  connectNulls
                />

                {/* Current Pace projection (dashed gray - darker for visibility) */}
                <Line
                  type="monotone"
                  dataKey="currentPace"
                  stroke="#6b7280"
                  strokeWidth={2}
                  strokeDasharray="6 3"
                  name="Current Pace"
                  dot={false}
                  connectNulls
                />

                {/* Actual Revenue (solid blue) */}
                <Line
                  type="monotone"
                  dataKey="actual"
                  stroke="#3b82f6"
                  strokeWidth={3}
                  name="Actual"
                  dot={(props) => {
                    const { cx, cy, payload } = props;
                    if (payload.isToday && cy) {
                      return (
                        <circle
                          cx={cx}
                          cy={cy}
                          r={5}
                          fill="#3b82f6"
                          stroke="white"
                          strokeWidth={2}
                        />
                      );
                    }
                    return null;
                  }}
                  connectNulls
                />
              </LineChart>
            </ResponsiveContainer>

            {/* Legend */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '24px',
              marginTop: '12px',
              fontSize: '12px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div style={{ width: '24px', height: '3px', background: '#3b82f6', borderRadius: '2px' }} />
                <span>Actual Revenue</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div style={{
                  width: '24px',
                  height: '3px',
                  background: 'repeating-linear-gradient(90deg, #6b7280 0px, #6b7280 4px, transparent 4px, transparent 8px)',
                  borderRadius: '2px'
                }} />
                <span>Current Pace (projected)</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div style={{
                  width: '24px',
                  height: '3px',
                  background: 'repeating-linear-gradient(90deg, #10b981 0px, #10b981 4px, transparent 4px, transparent 8px)',
                  borderRadius: '2px'
                }} />
                <span>Required Pace to Goal</span>
              </div>
            </div>

            {/* Gap indicator */}
            {revenuePacingData.gapToGoal > 0 && (
              <div style={{
                textAlign: 'center',
                marginTop: '8px',
                fontSize: '14px',
                color: '#ef4444',
                fontWeight: 500
              }}>
                At current pace, you'll end {formatCurrency(revenuePacingData.gapToGoal)} short of goal ({revenuePacingData.percentOfGoal.toFixed(0)}%)
              </div>
            )}
            {revenuePacingData.gapToGoal <= 0 && (
              <div style={{
                textAlign: 'center',
                marginTop: '8px',
                fontSize: '14px',
                color: '#10b981',
                fontWeight: 500
              }}>
                On track to exceed goal by {formatCurrency(Math.abs(revenuePacingData.gapToGoal))} ({revenuePacingData.percentOfGoal.toFixed(0)}%)
              </div>
            )}
          </div>
        )}

        <div style={{
          textAlign: 'center',
          fontSize: '11px',
          color: '#94a3b8',
          marginTop: '8px',
          fontStyle: 'italic'
        }}>
          Activity baseline: 6-month rolling average
        </div>
      </div>

      {/* ========== SECTION 2: PATH TO GOAL ========== */}
      <div className="overview-path-to-goal" style={{
        background: 'linear-gradient(135deg, #fefce8 0%, #fef9c3 100%)',
        border: '1px solid #fde047',
        borderRadius: '12px',
        padding: '24px',
        marginBottom: '20px',
        position: 'relative'
      }}>
        {developerMode && <div className="dev-section-label">S-2</div>}

        {/* Section Header */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{
            fontSize: '22px',
            fontWeight: 700,
            color: '#1e293b',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            🎯 Path to Goal
          </div>
          <div style={{
            fontSize: '15px',
            color: '#64748b',
            marginTop: '2px'
          }}>
            Current revenue, projected pace, and gap to goal
          </div>
        </div>

        {/* 3-Card Row: Current, Projected, Gap to Goal */}
        {pathToGoalData && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '16px',
            marginBottom: '24px'
          }}>
            {/* Card 1: CURRENT (Blue theme) */}
            <div style={{
              background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
              borderRadius: '10px',
              padding: '20px',
              borderLeft: '4px solid #3b82f6',
              position: 'relative'
            }}>
              {developerMode && <div className="dev-card-label">P-1</div>}
              <h4 style={{ fontSize: '14px', fontWeight: 600, color: '#1e40af', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                💰 Current
              </h4>
              <div style={{ fontSize: '32px', fontWeight: 700, color: '#1e293b', marginBottom: '12px' }}>
                {formatCurrencyFull(pathToGoalData.revenue)}
              </div>
              <div style={{ fontSize: '13px', color: '#475569', lineHeight: '1.6' }}>
                <div>{pathToGoalData.funded} funded</div>
                <div>{pathToGoalData.approved} approved</div>
                <div>{pathToGoalData.apps} apps</div>
              </div>
            </div>

            {/* Card 2: PROJECTED (Gray theme) */}
            <div style={{
              background: '#f8fafc',
              borderRadius: '10px',
              padding: '20px',
              borderLeft: '4px solid #94a3b8',
              position: 'relative'
            }}>
              {developerMode && <div className="dev-card-label">P-2</div>}
              <h4 style={{ fontSize: '14px', fontWeight: 600, color: '#64748b', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                📈 Projected
              </h4>
              <div style={{ fontSize: '32px', fontWeight: 700, color: '#1e293b', marginBottom: '12px' }}>
                {formatCurrencyFull(pathToGoalData.projectedRevenue)}
              </div>
              <div style={{ fontSize: '13px', color: '#475569', lineHeight: '1.6' }}>
                <div>{pathToGoalData.projectedFunded} funded</div>
                <div>{pathToGoalData.projectedApproved} approved</div>
                <div>{pathToGoalData.projectedApps} apps</div>
              </div>
              <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '8px', fontStyle: 'italic' }}>
                End of month if nothing changes
              </div>
            </div>

            {/* Card 3: GAP TO GOAL (Red if behind, Green if ahead) */}
            <div style={{
              background: pathToGoalData.gapToGoal > 0
                ? 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)'
                : 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
              borderRadius: '10px',
              padding: '20px',
              borderLeft: `4px solid ${pathToGoalData.gapToGoal > 0 ? '#ef4444' : '#22c55e'}`,
              position: 'relative'
            }}>
              {developerMode && <div className="dev-card-label">P-3</div>}
              <h4 style={{
                fontSize: '14px',
                fontWeight: 600,
                color: pathToGoalData.gapToGoal > 0 ? '#991b1b' : '#166534',
                marginBottom: '12px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                🎯 Gap to Goal
              </h4>
              <div style={{
                fontSize: '32px',
                fontWeight: 700,
                color: pathToGoalData.gapToGoal > 0 ? '#ef4444' : '#22c55e',
                marginBottom: '12px'
              }}>
                {pathToGoalData.gapToGoal > 0 ? '-' : '+'}{formatCurrencyFull(Math.abs(pathToGoalData.gapToGoal))}
              </div>
              <div style={{
                fontSize: '13px',
                color: pathToGoalData.gapToGoal > 0 ? '#991b1b' : '#166534',
                lineHeight: '1.6'
              }}>
                {pathToGoalData.gapToGoal > 0 ? (
                  <>
                    <div>Need {pathToGoalData.dealsNeeded} more deals</div>
                    <div>at {formatCurrencyFull(pathToGoalData.avgDealSize)} avg</div>
                  </>
                ) : (
                  <div>On track to exceed goal!</div>
                )}
              </div>
              <div style={{ fontSize: '12px', color: '#64748b', marginTop: '8px' }}>
                {pathToGoalData.percentOfGoal.toFixed(0)}% of goal
              </div>
            </div>
          </div>
        )}

        {/* Visual Funnel */}
        {pathToGoalData && (
          <div style={{ marginBottom: '24px' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}>
              {[
                { label: 'Activity', value: formatNumber(pathToGoalData.activity), color: '#3b82f6', rate: null },
                { label: 'Apps', value: pathToGoalData.apps, color: '#10b981', rate: pathToGoalData.activityToAppsRate },
                { label: 'Approved', value: pathToGoalData.approved, color: '#8b5cf6', rate: pathToGoalData.appsToApprovedRate },
                { label: 'Funded', value: pathToGoalData.funded, color: '#f59e0b', rate: pathToGoalData.approvedToFundedRate },
                { label: 'Revenue', value: formatCurrency(pathToGoalData.revenue), color: '#10b981', rate: null }
              ].map((stage, i, arr) => (
                <React.Fragment key={stage.label}>
                  <div style={{
                    width: '100px',
                    height: '65px',
                    borderRadius: '8px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: stage.color,
                    color: 'white',
                    fontWeight: 600
                  }}>
                    <div style={{ fontSize: '22px', fontWeight: 700 }}>{stage.value}</div>
                    <div style={{ fontSize: '13px', fontWeight: 500, opacity: 0.9, textTransform: 'uppercase' }}>
                      {stage.label}
                    </div>
                  </div>
                  {i < arr.length - 1 && (
                    <div style={{ color: '#94a3b8', fontSize: '14px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <span>→</span>
                      <span style={{ fontSize: '13px', color: '#64748b', fontWeight: 600 }}>
                        {arr[i + 1].rate !== null
                          ? `${arr[i + 1].rate.toFixed(1)}%`
                          : (i === arr.length - 2 ? formatCurrency(pathToGoalData.avgDealSize) : '')}
                      </span>
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        )}

        {/* Conversion Rates Row */}
        {pathToGoalData && (
          <div style={{
            display: 'flex',
            justifyContent: 'space-around',
            background: 'white',
            borderRadius: '8px',
            padding: '12px',
            marginBottom: '24px',
            border: '1px solid #e2e8f0'
          }}>
            {[
              {
                label: 'Activity → Apps',
                current: pathToGoalData.activityToAppsRate,
                historical: pathToGoalData.histActToAppsRate,
                format: (v) => `${v.toFixed(1)}%`
              },
              {
                label: 'Apps → Approved',
                current: pathToGoalData.appsToApprovedRate,
                historical: pathToGoalData.histAppsToApprovedRate,
                format: (v) => `${v.toFixed(1)}%`
              },
              {
                label: 'Approved → Funded',
                current: pathToGoalData.approvedToFundedRate,
                historical: pathToGoalData.histApprovedToFundedRate,
                format: (v) => `${v.toFixed(1)}%`
              },
              {
                label: 'Avg Deal Size',
                current: pathToGoalData.avgDealSize,
                historical: pathToGoalData.histAvgDealSize,
                format: (v) => formatCurrency(v)
              }
            ].map(rate => {
              const diff = rate.current - rate.historical;
              const isPositive = diff >= 0;
              const showWarning = !isPositive && Math.abs(diff) > 5;
              return (
                <div key={rate.label} style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '14px', color: '#64748b' }}>{rate.label}</div>
                  <div style={{ fontSize: '24px', fontWeight: 700, color: '#1e293b' }}>
                    {rate.format(rate.current)}
                  </div>
                  <div style={{ fontSize: '14px', color: isPositive ? '#10b981' : '#ef4444' }}>
                    vs {rate.format(rate.historical)} avg {isPositive ? '✓' : showWarning ? '⚠️' : ''}
                  </div>
                </div>
              );
            })}
          </div>
        )}

      </div>

      {/* ========== SECTION 3: SCENARIO CALCULATOR ========== */}
      {pathToGoalData && calculatorProjections && (
        <div className="overview-scenarios" style={{
          background: '#fefce8',
          borderRadius: '12px',
          padding: '24px',
          border: '1px solid #fde047',
          marginBottom: '20px',
          position: 'relative'
        }}>
          {developerMode && <div className="dev-section-label">S-3</div>}

          {/* Section Header */}
          <div style={{ marginBottom: '20px' }}>
            <div style={{
              fontSize: '22px',
              fontWeight: 700,
              color: '#1e293b',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              🧪 Scenario Calculator
            </div>
            <div style={{
              fontSize: '15px',
              color: '#64748b',
              marginTop: '2px'
            }}>
              Click a scenario or adjust sliders to explore projections
            </div>
          </div>

          {/* TOP: Smart Scenarios (moved from S-2 in Phase 8) */}
          {pathToGoalData.gapToGoal > 0 && (
            <div style={{ marginBottom: '24px' }}>
              <div style={{
                fontSize: '15px',
                fontWeight: 600,
                color: '#1e293b',
                marginBottom: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  💡 Quick Scenarios
                </span>
                {activeScenario && (
                  <button
                    onClick={handleScenarioReset}
                    style={{
                      padding: '6px 12px',
                      background: '#f1f5f9',
                      border: '1px solid #cbd5e1',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      color: '#475569',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      fontWeight: 500
                    }}
                  >
                    ↩️ Reset
                  </button>
                )}
              </div>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '16px'
              }}>
                {/* Scenario A: Volume Play */}
                <div
                  onClick={handleScenarioA}
                  style={{
                    background: 'white',
                    borderRadius: '10px',
                    padding: '16px',
                    border: activeScenario === 'A' ? '3px solid #3b82f6' : '2px solid #e2e8f0',
                    borderLeft: '4px solid #3b82f6',
                    position: 'relative',
                    cursor: 'pointer',
                    boxShadow: activeScenario === 'A' ? '0 0 0 3px rgba(59, 130, 246, 0.2)' : 'none',
                    transition: 'all 0.2s ease'
                  }}
                >
                  {developerMode && <div className="dev-card-label">SC-A</div>}
                  {activeScenario === 'A' && (
                    <div style={{
                      position: 'absolute',
                      top: '8px',
                      right: '8px',
                      background: '#3b82f6',
                      color: 'white',
                      padding: '2px 8px',
                      borderRadius: '12px',
                      fontSize: '11px',
                      fontWeight: 600
                    }}>
                      ACTIVE
                    </div>
                  )}
                  <h5 style={{ fontSize: '14px', fontWeight: 600, color: '#64748b', marginBottom: '6px' }}>
                    📞 Volume Play
                  </h5>
                  <div style={{ fontSize: '28px', fontWeight: 700, color: '#10b981', marginBottom: '6px' }}>
                    +{formatCurrency(pathToGoalData.scenarioA.impact)}
                  </div>
                  <div style={{ fontSize: '13px', color: '#64748b', lineHeight: 1.5 }}>
                    +20% activity → +{pathToGoalData.scenarioA.extraFunded} funded<br/>
                    Closes {pathToGoalData.scenarioA.closesPercent.toFixed(0)}% of gap
                  </div>
                </div>

                {/* Scenario B: Conversion Fix */}
                <div
                  onClick={!pathToGoalData.scenarioB.isAllHealthy ? handleScenarioB : undefined}
                  style={{
                    background: 'white',
                    borderRadius: '10px',
                    padding: '16px',
                    border: activeScenario === 'B' ? '3px solid #22c55e' : '2px solid #e2e8f0',
                    borderLeft: `4px solid ${pathToGoalData.scenarioB.isAllHealthy ? '#22c55e' : '#10b981'}`,
                    position: 'relative',
                    cursor: pathToGoalData.scenarioB.isAllHealthy ? 'default' : 'pointer',
                    opacity: pathToGoalData.scenarioB.isAllHealthy ? 0.7 : 1,
                    boxShadow: activeScenario === 'B' ? '0 0 0 3px rgba(34, 197, 94, 0.2)' : 'none',
                    transition: 'all 0.2s ease'
                  }}
                >
                  {developerMode && <div className="dev-card-label">SC-B</div>}
                  {activeScenario === 'B' && (
                    <div style={{
                      position: 'absolute',
                      top: '8px',
                      right: '8px',
                      background: '#22c55e',
                      color: 'white',
                      padding: '2px 8px',
                      borderRadius: '12px',
                      fontSize: '11px',
                      fontWeight: 600
                    }}>
                      ACTIVE
                    </div>
                  )}
                  {pathToGoalData.scenarioB.isAllHealthy ? (
                    <>
                      <h5 style={{ fontSize: '14px', fontWeight: 600, color: '#22c55e', marginBottom: '6px' }}>
                        🎉 All Healthy
                      </h5>
                      <div style={{ fontSize: '16px', fontWeight: 600, color: '#22c55e', marginBottom: '6px' }}>
                        No issues!
                      </div>
                      <div style={{ fontSize: '13px', color: '#64748b', lineHeight: 1.5 }}>
                        All conversions at or above average
                      </div>
                    </>
                  ) : (
                    <>
                      <h5 style={{ fontSize: '14px', fontWeight: 600, color: '#64748b', marginBottom: '6px' }}>
                        🔧 Fix {pathToGoalData.scenarioB.conversionName}
                      </h5>
                      <div style={{ fontSize: '28px', fontWeight: 700, color: '#10b981', marginBottom: '6px' }}>
                        +{formatCurrency(pathToGoalData.scenarioB.impact)}
                      </div>
                      <div style={{ fontSize: '13px', color: '#64748b', lineHeight: 1.5 }}>
                        {pathToGoalData.scenarioB.currentRate.toFixed(1)}% → {pathToGoalData.scenarioB.targetRate.toFixed(1)}%<br/>
                        Closes {pathToGoalData.scenarioB.closesPercent.toFixed(0)}% of gap
                      </div>
                    </>
                  )}
                </div>

                {/* Scenario C: Hybrid */}
                <div
                  onClick={handleScenarioC}
                  style={{
                    background: 'white',
                    borderRadius: '10px',
                    padding: '16px',
                    border: activeScenario === 'C' ? '3px solid #8b5cf6' : '2px solid #e2e8f0',
                    borderLeft: '4px solid #8b5cf6',
                    position: 'relative',
                    cursor: 'pointer',
                    boxShadow: activeScenario === 'C' ? '0 0 0 3px rgba(139, 92, 246, 0.2)' : 'none',
                    transition: 'all 0.2s ease'
                  }}
                >
                  {developerMode && <div className="dev-card-label">SC-C</div>}
                  {activeScenario === 'C' && (
                    <div style={{
                      position: 'absolute',
                      top: '8px',
                      right: '8px',
                      background: '#8b5cf6',
                      color: 'white',
                      padding: '2px 8px',
                      borderRadius: '12px',
                      fontSize: '11px',
                      fontWeight: 600
                    }}>
                      ACTIVE
                    </div>
                  )}
                  <h5 style={{ fontSize: '14px', fontWeight: 600, color: '#64748b', marginBottom: '6px' }}>
                    ⚡ Hybrid
                  </h5>
                  <div style={{ fontSize: '28px', fontWeight: 700, color: '#10b981', marginBottom: '6px' }}>
                    +{formatCurrency(pathToGoalData.scenarioC.impact)}
                  </div>
                  <div style={{ fontSize: '13px', color: '#64748b', lineHeight: 1.5 }}>
                    Volume + Fix conversion<br/>
                    <strong>{formatCurrency(pathToGoalData.scenarioC.projectedTotal)} ({pathToGoalData.scenarioC.projectedPercent.toFixed(0)}%)</strong>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* On track message when gap is closed */}
          {pathToGoalData.gapToGoal <= 0 && (
            <div style={{
              background: '#f0fdf4',
              border: '1px solid #bbf7d0',
              borderRadius: '10px',
              padding: '20px',
              marginBottom: '24px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '28px', marginBottom: '8px' }}>🎉</div>
              <div style={{ fontSize: '18px', fontWeight: 600, color: '#16a34a' }}>
                On Track to Exceed Goal!
              </div>
              <div style={{ fontSize: '15px', color: '#15803d', marginTop: '4px' }}>
                At current pace, you'll end {formatCurrency(Math.abs(pathToGoalData.gapToGoal))} above goal.
              </div>
            </div>
          )}

          {/* SECTION: Current Pace (Baseline) */}
          <div style={{
            background: '#f8fafc',
            borderRadius: '10px',
            padding: '16px',
            marginBottom: '16px',
            border: '1px solid #e2e8f0'
          }}>
            <div style={{
              fontSize: '14px',
              fontWeight: 600,
              color: '#64748b',
              marginBottom: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}>
              📊 Current Pace
            </div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: '12px'
            }}>
              {[
                { label: 'Apps', value: pathToGoalData.projectedApps, isCurrency: false },
                { label: 'Approved', value: pathToGoalData.projectedApproved, isCurrency: false },
                { label: 'Funded', value: pathToGoalData.projectedFunded, isCurrency: false },
                { label: 'Revenue', value: pathToGoalData.projectedRevenue, isCurrency: true }
              ].map((card, i) => (
                <div key={card.label} style={{
                  background: 'white',
                  borderRadius: '8px',
                  padding: '12px',
                  border: '1px solid #e2e8f0',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '11px', color: '#94a3b8', textTransform: 'uppercase', marginBottom: '4px' }}>
                    {card.label}
                  </div>
                  <div style={{ fontSize: '22px', fontWeight: 600, color: '#64748b' }}>
                    {card.isCurrency ? formatCurrency(card.value) : Math.round(card.value)}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* SECTION: With This Scenario (Impact) */}
          <div style={{
            background: 'white',
            borderRadius: '10px',
            padding: '16px',
            marginBottom: '24px',
            border: '1px solid #e2e8f0'
          }}>
            <div style={{
              fontSize: '14px',
              fontWeight: 600,
              color: '#1e293b',
              marginBottom: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}>
              🎯 With This Scenario
            </div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: '12px'
            }}>
              {[
                { label: 'Apps', baseline: pathToGoalData.projectedApps, scenario: calculatorProjections.projApps, color: '#3b82f6', isCurrency: false },
                { label: 'Approved', baseline: pathToGoalData.projectedApproved, scenario: calculatorProjections.projApproved, color: '#8b5cf6', isCurrency: false },
                { label: 'Funded', baseline: pathToGoalData.projectedFunded, scenario: calculatorProjections.projFunded, color: '#f59e0b', isCurrency: false },
                { label: 'Revenue', baseline: pathToGoalData.projectedRevenue, scenario: calculatorProjections.projRevenue, color: '#10b981', isCurrency: true }
              ].map((card, i) => {
                const delta = card.scenario - card.baseline;
                const deltaColor = delta > 0 ? '#10b981' : delta < 0 ? '#ef4444' : '#64748b';
                const deltaIcon = delta > 0 ? '▲' : delta < 0 ? '▼' : '';
                const deltaPrefix = delta >= 0 ? '+' : '';

                return (
                  <div key={card.label} style={{
                    background: '#f8fafc',
                    borderRadius: '8px',
                    padding: '12px',
                    border: '1px solid #e2e8f0',
                    textAlign: 'center',
                    position: 'relative'
                  }}>
                    {developerMode && <div className="dev-card-label">SC-{i + 1}</div>}
                    <div style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', marginBottom: '4px' }}>
                      {card.label}
                    </div>
                    <div style={{ fontSize: '28px', fontWeight: 700, color: card.color }}>
                      {card.isCurrency ? formatCurrency(card.scenario) : Math.round(card.scenario)}
                    </div>
                    <div style={{
                      fontSize: '14px',
                      fontWeight: 600,
                      color: deltaColor,
                      marginTop: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '4px'
                    }}>
                      {deltaPrefix}{card.isCurrency ? formatCurrency(Math.abs(delta)) : Math.round(Math.abs(delta))} {deltaIcon}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Gap Remaining - Now before sliders */}
          <div style={{
            background: 'white',
            borderRadius: '10px',
            padding: '20px',
            border: '1px solid #e2e8f0',
            textAlign: 'center',
            marginBottom: '24px'
          }}>
            <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '8px' }}>Gap Remaining</div>
            <div style={{
              fontSize: '32px',
              fontWeight: 700,
              color: calculatorProjections.gapRemaining > 0 ? '#f59e0b' : '#10b981'
            }}>
              {calculatorProjections.gapRemaining > 0
                ? `-${formatCurrency(calculatorProjections.gapRemaining)}`
                : `+${formatCurrency(Math.abs(calculatorProjections.gapRemaining))}`
              }
            </div>
            <div style={{ fontSize: '14px', color: '#64748b', marginTop: '4px' }}>
              {calculatorProjections.percentToGoal.toFixed(0)}% to goal
            </div>
          </div>

          {/* Fine-Tune Sliders - De-emphasized at bottom (Phase 8) */}
          <div style={{
            background: '#f8fafc',
            borderRadius: '10px',
            padding: '16px',
            border: '1px solid #e2e8f0'
          }}>
            <div style={{
              fontSize: '14px',
              fontWeight: 500,
              color: '#64748b',
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}>
              🎚️ Fine-Tune Rates
            </div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '12px'
            }}>
              {/* Activity Increase Slider */}
              <EnhancedSlider
                label="Activity Increase"
                icon="📞"
                currentValue={0}
                adjustedValue={calculatorInputs.activityIncrease}
                onAdjust={(val) => {
                  setCalculatorInputs(prev => ({ ...prev, activityIncrease: Math.round(val) }));
                  setActiveScenario(null);
                }}
                historicalAvg={0}
                min={0}
                max={50}
                step={5}
                formatValue={(v) => `+${v}%`}
                formatChange={(v) => `${v > 0 ? '+' : ''}${v}%`}
              />

              {/* Activity → Apps Rate Slider */}
              <EnhancedSlider
                label="Activity → Apps Rate"
                icon="📋"
                currentValue={pathToGoalData.activityToAppsRate}
                adjustedValue={calculatorInputs.actToAppsRate ?? pathToGoalData.activityToAppsRate}
                onAdjust={(val) => {
                  setCalculatorInputs(prev => ({ ...prev, actToAppsRate: val }));
                  setActiveScenario(null);
                }}
                historicalAvg={pathToGoalData.histActToAppsRate}
                min={0.5}
                max={5}
                step={0.1}
                formatValue={(v) => `${v.toFixed(1)}%`}
                formatChange={(v) => `${v > 0 ? '+' : ''}${v.toFixed(1)}pp`}
              />

              {/* Apps → Approved Rate Slider */}
              <EnhancedSlider
                label="Apps → Approved Rate"
                icon="✅"
                currentValue={pathToGoalData.appsToApprovedRate}
                adjustedValue={calculatorInputs.appsToApprovedRate ?? pathToGoalData.appsToApprovedRate}
                onAdjust={(val) => {
                  setCalculatorInputs(prev => ({ ...prev, appsToApprovedRate: val }));
                  setActiveScenario(null);
                }}
                historicalAvg={pathToGoalData.histAppsToApprovedRate}
                min={20}
                max={90}
                step={1}
                formatValue={(v) => `${v.toFixed(1)}%`}
                formatChange={(v) => `${v > 0 ? '+' : ''}${v.toFixed(1)}pp`}
              />

              {/* Approved → Funded Rate Slider */}
              <EnhancedSlider
                label="Approved → Funded Rate"
                icon="💰"
                currentValue={pathToGoalData.approvedToFundedRate}
                adjustedValue={calculatorInputs.approvedToFundedRate ?? pathToGoalData.approvedToFundedRate}
                onAdjust={(val) => {
                  setCalculatorInputs(prev => ({ ...prev, approvedToFundedRate: val }));
                  setActiveScenario(null);
                }}
                historicalAvg={pathToGoalData.histApprovedToFundedRate}
                min={20}
                max={90}
                step={1}
                formatValue={(v) => `${v.toFixed(1)}%`}
                formatChange={(v) => `${v > 0 ? '+' : ''}${v.toFixed(1)}pp`}
              />
            </div>
          </div>
        </div>
      )}

      {/* ========== SECTION 4: THE DIAGNOSIS ========== */}
      <div className="overview-diagnosis" style={{
        background: 'white',
        borderRadius: '12px',
        padding: '24px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        border: '1px solid #e2e8f0',
        marginBottom: '20px',
        position: 'relative'
      }}>
        {developerMode && <div className="dev-section-label">S-4</div>}

        {/* Section Header */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{
            fontSize: '22px',
            fontWeight: 700,
            color: '#1e293b',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            🔍 The Diagnosis
          </div>
          <div style={{
            fontSize: '15px',
            color: '#64748b',
            marginTop: '2px'
          }}>
            {pathToGoalData
              ? `Why you're at ${formatCurrencyFull(pathToGoalData.revenue)} of ${formatCurrencyFull(pathToGoalData.revenueGoal)} goal`
              : 'Analyzing your conversion funnel...'}
          </div>
        </div>

        {/* 4-Panel Diagnosis Grid */}
        {conversionAnalysis ? (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '16px'
          }}>
            {/* Panel 1: PRIMARY ISSUE (Red) */}
            <div style={{
              padding: '16px',
              borderRadius: '8px',
              background: conversionAnalysis.worstConversion ? '#fef2f2' : '#f0fdf4',
              border: `1px solid ${conversionAnalysis.worstConversion ? '#fecaca' : '#bbf7d0'}`,
              borderLeft: `4px solid ${conversionAnalysis.worstConversion ? '#ef4444' : '#22c55e'}`,
              position: 'relative'
            }}>
              {developerMode && <div className="dev-card-label">D-1</div>}
              {conversionAnalysis.worstConversion ? (
                <>
                  <h5 style={{
                    fontSize: '14px',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    color: '#dc2626',
                    marginBottom: '12px',
                    letterSpacing: '0.5px'
                  }}>
                    🔴 Primary Issue
                  </h5>
                  <div style={{ fontSize: '20px', fontWeight: 700, color: '#1e293b', marginBottom: '8px' }}>
                    {conversionAnalysis.worstConversion.name}
                  </div>
                  <div style={{ fontSize: '15px', color: '#64748b', marginBottom: '12px' }}>
                    {conversionAnalysis.worstConversion.currentRate.toFixed(1)}% vs {conversionAnalysis.worstConversion.historicalRate.toFixed(1)}% avg
                  </div>
                  <div style={{ fontSize: '18px', fontWeight: 600, color: '#ef4444', marginBottom: '4px' }}>
                    Revenue Impact: -${conversionAnalysis.worstConversion.revenueImpact.toLocaleString()}
                  </div>
                  <div style={{ fontSize: '14px', color: '#64748b' }}>
                    Lost Deals: ~{Math.round(conversionAnalysis.worstConversion.extraDeals)} funded
                  </div>
                </>
              ) : (
                <>
                  <h5 style={{
                    fontSize: '14px',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    color: '#16a34a',
                    marginBottom: '12px',
                    letterSpacing: '0.5px'
                  }}>
                    🎉 No Major Issues
                  </h5>
                  <div style={{ fontSize: '15px', color: '#166534', lineHeight: 1.6 }}>
                    All conversion rates are at or above your 6-month average! Focus on increasing activity volume to drive more revenue.
                  </div>
                </>
              )}
            </div>

            {/* Panel 2: SECONDARY ISSUES (Amber) */}
            <div style={{
              padding: '16px',
              borderRadius: '8px',
              background: '#fffbeb',
              border: '1px solid #fde68a',
              borderLeft: '4px solid #f59e0b',
              position: 'relative'
            }}>
              {developerMode && <div className="dev-card-label">D-2</div>}
              <h5 style={{
                fontSize: '14px',
                fontWeight: 700,
                textTransform: 'uppercase',
                color: '#b45309',
                marginBottom: '12px',
                letterSpacing: '0.5px'
              }}>
                ⚠️ Secondary Issues
              </h5>
              {(() => {
                const secondaryIssues = conversionAnalysis.issues?.slice(1) || [];
                if (secondaryIssues.length === 0) {
                  return (
                    <div style={{ fontSize: '15px', color: '#92400e', fontStyle: 'italic' }}>
                      {conversionAnalysis.worstConversion
                        ? 'None - only one issue identified'
                        : 'None - all conversions are healthy!'}
                    </div>
                  );
                }
                return (
                  <>
                    {secondaryIssues.map(issue => (
                      <div key={issue.name} style={{ fontSize: '15px', color: '#374151', padding: '4px 0' }}>
                        • <strong>{issue.name}</strong>: -${issue.revenueImpact.toLocaleString()}
                        <div style={{ fontSize: '13px', color: '#64748b', marginLeft: '12px' }}>
                          ({issue.currentRate.toFixed(1)}% vs {issue.historicalRate.toFixed(1)}% avg)
                        </div>
                      </div>
                    ))}
                    <div style={{ fontSize: '14px', fontWeight: 600, color: '#b45309', marginTop: '12px', paddingTop: '8px', borderTop: '1px solid #fde68a' }}>
                      Total Secondary: -${secondaryIssues.reduce((sum, i) => sum + i.revenueImpact, 0).toLocaleString()}
                    </div>
                  </>
                );
              })()}
            </div>

            {/* Panel 3: BRIGHT SPOTS (Green) */}
            <div style={{
              padding: '16px',
              borderRadius: '8px',
              background: '#f0fdf4',
              border: '1px solid #bbf7d0',
              borderLeft: '4px solid #22c55e',
              position: 'relative'
            }}>
              {developerMode && <div className="dev-card-label">D-3</div>}
              <h5 style={{
                fontSize: '14px',
                fontWeight: 700,
                textTransform: 'uppercase',
                color: '#16a34a',
                marginBottom: '12px',
                letterSpacing: '0.5px'
              }}>
                ✅ Bright Spots
              </h5>
              {conversionAnalysis.brightSpots?.length > 0 ? (
                conversionAnalysis.brightSpots.map(spot => (
                  <div key={spot.name} style={{ fontSize: '15px', color: '#374151', padding: '4px 0' }}>
                    • <strong>{spot.name}</strong>: {spot.currentRate.toFixed(1)}% vs {spot.historicalRate.toFixed(1)}% avg
                    <span style={{ color: '#16a34a', fontWeight: 600, marginLeft: '8px' }}>
                      (+{Math.abs(spot.gap).toFixed(1)}pp)
                    </span>
                  </div>
                ))
              ) : (
                <div style={{ fontSize: '15px', color: '#6b7280', fontStyle: 'italic' }}>
                  None currently - all conversions below average
                </div>
              )}
            </div>

            {/* Panel 4: THE FULL PICTURE (Blue/Gray) */}
            <div style={{
              padding: '16px',
              borderRadius: '8px',
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderLeft: '4px solid #3b82f6',
              position: 'relative'
            }}>
              {developerMode && <div className="dev-card-label">D-4</div>}
              <h5 style={{
                fontSize: '14px',
                fontWeight: 700,
                textTransform: 'uppercase',
                color: '#1d4ed8',
                marginBottom: '12px',
                letterSpacing: '0.5px'
              }}>
                📊 The Full Picture
              </h5>
              <div style={{ fontSize: '15px', color: '#374151', lineHeight: 1.7 }}>
                {(() => {
                  const primary = conversionAnalysis.worstConversion;
                  const brightSpots = conversionAnalysis.brightSpots || [];

                  if (!primary) {
                    return "All your conversion rates are healthy! Focus on increasing activity volume to drive more revenue.";
                  }

                  let narrative = `Your ${primary.name} rate is costing you ~$${primary.revenueImpact.toLocaleString()} this month. `;
                  narrative += `If you returned to your 6-month average of ${primary.historicalRate.toFixed(1)}%, `;
                  narrative += `you'd have ~${Math.round(primary.extraDeals)} more funded deals.`;

                  if (brightSpots.length > 0) {
                    narrative += ` The good news: your ${brightSpots[0].name} is strong at ${brightSpots[0].currentRate.toFixed(1)}% (above your ${brightSpots[0].historicalRate.toFixed(1)}% average).`;
                  }

                  return narrative;
                })()}
              </div>
            </div>
          </div>
        ) : (
          /* Loading state */
          <div style={{
            textAlign: 'center',
            padding: '40px',
            color: '#94a3b8'
          }}>
            Loading diagnosis...
          </div>
        )}
      </div>
    </div>
  );
}
